import * as Sentry from '@sentry/react'

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1'

function generateRequestId(): string {
  return crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 10)
}

export class ApiError extends Error {
  public status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

/**
 * BFF auth (WEB-408): authentication is the httpOnly session cookie set by the
 * backend OIDC callback. The browser never holds an access token — there is
 * nothing in localStorage for an XSS to steal (the WEB-313 exposure is gone).
 *
 * For state-changing requests we send the double-submit CSRF token: the backend
 * mirrors the session CSRF token into a readable `gcg_csrf` cookie, and we echo
 * it back in the `X-CSRF-Token` header. SameSite=Lax covers the rest.
 */
const CSRF_COOKIE = 'gcg_csrf'
const CSRF_HEADER = 'X-CSRF-Token'

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * CSRF header for state-changing requests that go straight to the BFF (the
 * `/auth/*` routes, which are not under `/api/v1` and so bypass apiFetch).
 */
export function csrfHeaders(): Record<string, string> {
  const csrf = readCookie(CSRF_COOKIE)
  return csrf ? { [CSRF_HEADER]: csrf } : {}
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) ?? {}),
  }

  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE)
    if (csrf) headers[CSRF_HEADER] = csrf
  }

  const requestId = generateRequestId()
  headers['X-Request-ID'] = requestId
  Sentry.setTag('request_id', requestId)

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers,
  })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new ApiError(res.status, text)
  }
  // 204 No Content (DELETE responses) — return undefined cast to T
  if (res.status === 204 || res.headers.get('Content-Length') === '0') {
    return undefined as unknown as T
  }
  return res.json() as Promise<T>
}
